import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { countries } from "./CountryName";
import {
  MapPin, Plus, Trash2, Calendar,
  Globe, Clock, CheckCircle2, AlertCircle,
  Home, ChevronRight, History, Building2
} from "lucide-react";

const getEmptyAddress = () => ({
  address: "",
  country: "",
  from: "",
  to: "",
});

const AddressHistoryForm = () => {
  const [addresses, setAddresses] = useState([getEmptyAddress()]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    fetchAddressHistory();
  }, []);

  const fetchAddressHistory = async () => {
    setLoading(true);
    try {
      const res = await makeRequestAuth("get", "/user/getAddressHistory");
      if (res?.data?.addresses?.length) {
        setAddresses(res.data.addresses);
        setPreview(res.data);
      }
    } catch {
      console.log("No address history found");
    } finally {
      setLoading(false);
    }
  };

  const validateAddresses = () => {
    let errorList = [];
    addresses.forEach((a, index) => {
      if (!a.address.trim()) errorList.push(`Address ${index + 1}: Address is required`);
      if (!a.country.trim()) errorList.push(`Address ${index + 1}: Country is required`);
      if (!a.from) errorList.push(`Address ${index + 1}: From date is required`);
      if (!a.to) errorList.push(`Address ${index + 1}: To date is required`);
      if (a.from && a.to && new Date(a.from) > new Date(a.to))
        errorList.push(`Address ${index + 1}: From date cannot be greater than To date`);
    });
    return errorList;
  };

  const handleChange = (index, field, value) => {
    setAddresses((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addAddress = () => setAddresses((prev) => [...prev, getEmptyAddress()]);
  const removeAddress = (index) => setAddresses((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateAddresses();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo(0, 0);
      return;
    }
    setErrors([]);
    setLoading(true);
    try {
      const res = await makeRequestAuth("post", "/user/createOrUpdateAddressHistory", { addresses });
      setPreview(res?.data);
      showSuccessToast("Address history details saved successfully ✅");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: <Clock size={16} /> },
    approve: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <CheckCircle2 size={16} /> },
    reject: { color: "bg-rose-50 text-rose-700 border-rose-200", icon: <AlertCircle size={16} /> },
  };

  return (
    <div className="max-w-4xl mx-auto py-6 antialiased font-normal">
      <ToastContainer />

      {/* --- PREMIUM LOADER --- */}
      {loading && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-[2rem] shadow-2xl flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin"></div>
            <span className="font-normal text-[#132b45] uppercase tracking-widest text-xs">Syncing Timeline...</span>
          </div>
        </div>
      )}

      {/* --- ERROR SUMMARY --- */}
      {errors.length > 0 && (
        <div className="mb-8 bg-rose-50 border border-rose-200 rounded-3xl p-5 animate-in slide-in-from-top-4">
          <div className="flex items-center gap-2 text-rose-700 mb-2 font-medium uppercase text-xs">
            <AlertCircle size={18} /> Error Found in Timeline
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 list-inside">
            {errors.map((err, i) => <li key={i} className="text-rose-600 text-[13px] font-normal">{err}</li>)}
          </ul>
        </div>
      )}

      {preview ? (
        /* ================= PREVIEW MODE (TIMELINE VIEW) ================= */
        <div className="space-y-8 animate-in fade-in duration-500">
          <div className="flex justify-between items-center px-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 bg-[#132b45] text-white rounded-2xl flex items-center justify-center shadow-lg">
                <History size={28} />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase">
                  Residency Audit Trail
                </h3>
                <p className="text-xs text-slate-400 font-bold tracking-widest flex items-center gap-1.5 uppercase">
                  <Building2 size={14} className="text-indigo-500" /> Historical
                  Address Verification
                </p>
              </div>
            </div>
            <div className={`flex items-center gap-2 px-5 py-2 rounded-full border shadow-sm font-normal text-xs uppercase tracking-widest ${STATUS_CONFIG[preview?.status?.toLowerCase()]?.color || STATUS_CONFIG.pending.color}`}>
              {STATUS_CONFIG[preview?.status?.toLowerCase()]?.icon || STATUS_CONFIG.pending.icon}
              {preview?.status || "Pending"}
            </div>
          </div>

          <div className="space-y-8">
            {preview.addresses?.map((addr, i) => (
              <div
                key={addr._id || i}
                className="relative flex flex-col md:flex-row gap-6 md:pl-16 group"
              >
                {/* STEP INDICATOR */}
                <div className="absolute left-[-11px] top-6 hidden md:flex h-10 w-10 rounded-full bg-white border-4 border-indigo-600 text-indigo-600 items-center justify-center z-10 shadow-sm group-hover:scale-110 transition-transform">
                  <span className="text-[10px] font-black">{i + 1}</span>
                </div>

                <div className="flex-1 bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm hover:shadow-md hover:border-indigo-100 transition-all">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                        <MapPin size={18} />
                      </div>
                      <h5 className="font-black text-slate-700 uppercase tracking-widest text-xs">
                        Residence Entry {i + 1}
                      </h5>
                    </div>

                    <div className="flex items-center gap-2 bg-slate-50 px-4 py-1.5 rounded-full text-xs font-bold text-slate-500">
                      <Calendar size={14} className="text-slate-400" />
                      {formatDate(addr.from)}{" "}
                      <ChevronRight size={12} className="text-slate-300" />{" "}
                      {formatDate(addr.to)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Full Address
                      </span>
                      <p className="text-slate-700 font-bold text-sm leading-relaxed">
                        {addr.address || "—"}
                      </p>
                    </div>
                    <div>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Country
                      </span>
                      <div className="flex items-center gap-2 text-slate-700 font-bold text-sm">
                        <Globe size={14} className="text-indigo-400" />
                        {addr.country || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {preview?.status === "reject" && (
            <div className="flex justify-center pt-4">
              <button
                onClick={() => { setPreview(null); setAddresses([getEmptyAddress()]); }}
                className="bg-[#132b45] hover:bg-black text-white px-12 py-4 rounded-2xl font-normal shadow-xl transition-all active:scale-95"
              >
                EDIT TIMELINE
              </button>
            </div>
          )}
        </div>
      ) : (
        /* ================= FORM MODE ================= */
        <form onSubmit={handleSubmit} className="space-y-8 animate-in slide-in-from-bottom-8 duration-700">
          <header className="flex items-center gap-4 border-b border-slate-100 pb-6 px-4">
            <div className="p-3 bg-indigo-900 text-white rounded-2xl"><Home size={28} /></div>
            <div>
              <h2 className="text-2xl font-medium text-[#132b45]">Address History</h2>
              <p className="text-slate-400 text-sm font-normal italic">Please provide last 10 years of residency records</p>
            </div>
          </header>

          <div className="space-y-10">
            {addresses.map((address, index) => (
              <div key={index} className="bg-white rounded-[2.5rem] border border-slate-200 p-8 shadow-sm relative group animate-in zoom-in-95">
                <div className="flex justify-between items-center mb-8 border-b border-slate-50 pb-4">
                  <h3 className="flex items-center gap-2 text-indigo-900 font-normal uppercase tracking-widest text-xs">
                    <span className="w-8 h-8 rounded-full bg-indigo-900 text-white flex items-center justify-center text-[10px]">{index + 1}</span>
                    Location Details
                  </h3>
                  {addresses.length > 1 && (
                    <button type="button" onClick={() => removeAddress(index)} className="text-rose-500 hover:bg-rose-50 p-2 rounded-xl transition-colors flex items-center gap-1 text-[10px] font-normal uppercase">
                      <Trash2 size={16} /> Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div className="md:col-span-2">
                    <Input icon={<MapPin size={18} />} label="Full Residential Address *" placeholder="House No, Street, City, State" value={address.address} onChange={(v) => handleChange(index, "address", v)} />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-normal text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1.5"><Globe size={14} /> Country</label>
                    <select
                      value={address.country}
                      onChange={(e) => handleChange(index, "country", e.target.value)}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-3.5 focus:border-indigo-500 focus:bg-white outline-none transition-all font-normal text-[#132b45]"
                    >
                      <option value="">Select Country</option>
                      {countries.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Input type="date" label="From Date *" icon={<Calendar size={14} />} value={address.from} onChange={(v) => handleChange(index, "from", v)} />
                    <Input type="date" label="To Date *" icon={<Calendar size={14} />} value={address.to} onChange={(v) => handleChange(index, "to", v)} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col md:flex-row gap-4 px-4">
            <button
              type="button"
              onClick={addAddress}
              className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600 py-4 rounded-2xl font-normal text-sm transition-all"
            >
              <Plus size={20} /> ADD ANOTHER LOCATION
            </button>

            <button
              type="submit"
              className="flex-1 bg-[#132b45] hover:bg-black text-white py-4 rounded-2xl font-normal text-sm shadow-xl shadow-blue-900/10 transition-all active:scale-[0.98]"
            >
              SAVE ADDRESS HISTORY
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

/* --- MINI COMPONENTS --- */
const Input = ({ label, value, onChange, type = "text", icon, placeholder }) => (
  <div className="space-y-2">
    <label className="text-[10px] font-normal text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1.5">{icon}{label}</label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-3.5 focus:border-indigo-500 focus:bg-white outline-none transition-all font-normal text-[#132b45]"
    />
  </div>
);


export default AddressHistoryForm;